/**
 * LocationSearch Component
 *
 * Search for locations using OpenStreetMap's Nominatim geocoding service.
 * No API key required - free to use with proper attribution.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { AreaPreset } from '../types/events';
import { SearchIcon } from './icons';

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  class: string;
  importance: number;
}

interface LocationSearchProps {
  onLocationSelect: (location: AreaPreset) => void;
  placeholder?: string;
}

// Debounce delay for search (Nominatim requires max 1 request per second)
const SEARCH_DEBOUNCE_MS = 500;

/**
 * Determine appropriate radius based on location type.
 */
function getRadiusForLocationType(type: string, className: string): number {
  // Large areas
  if (className === 'boundary' || type === 'administrative') {
    return 100;
  }
  // Cities/towns
  if (type === 'city' || type === 'town') {
    return 50;
  }
  // Suburbs/villages
  if (type === 'suburb' || type === 'village' || type === 'neighbourhood') {
    return 25;
  }
  // Specific places
  if (type === 'hamlet' || type === 'locality') {
    return 15;
  }
  // Default
  return 30;
}

/**
 * Extract short name from display_name (first part before comma).
 */
function getShortName(displayName: string): string {
  const parts = displayName.split(',');
  return parts[0].trim();
}

export default function LocationSearch({ onLocationSelect, placeholder = 'Search for a location...' }: LocationSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Search for locations using Nominatim
  const searchLocations = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use Nominatim API - limit to Australia for relevance
      const params = new URLSearchParams({
        q: searchQuery,
        format: 'json',
        limit: '8',
        countrycodes: 'au', // Limit to Australia
        addressdetails: '1',
      });

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${params}`,
        {
          headers: {
            // Required by Nominatim usage policy
            'User-Agent': 'HipponicsFirewatch-BushfireApp/1.0',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data: NominatimResult[] = await response.json();
      setResults(data);
      setIsOpen(data.length > 0);
      setSelectedIndex(-1);
    } catch (err) {
      console.error('Location search error:', err);
      setError('Search failed. Please try again.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => {
        searchLocations(query);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      setResults([]);
      setIsOpen(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchLocations]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle result selection
  const handleSelect = (result: NominatimResult) => {
    const location: AreaPreset = {
      name: getShortName(result.display_name),
      lat: parseFloat(result.lat),
      lon: parseFloat(result.lon),
      defaultRadiusKm: getRadiusForLocationType(result.type, result.class),
    };

    onLocationSelect(location);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) {
      if (e.key === 'Enter' && query.length >= 2) {
        searchLocations(query);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  return (
    <div className="location-search" style={{ position: 'relative', marginBottom: '12px' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="search"
          inputMode="search"
          autoCorrect="off"
          autoCapitalize="words"
          enterKeyHint="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className="form-input"
          style={{
            width: '100%',
            padding: '8px 32px 8px 12px',
            // 16px floor prevents iOS auto-zoom on focus (P0-5). The mobile
            // App.css base rule already enforces this, but the inline style
            // would otherwise override it on iOS.
            fontSize: '16px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            outline: 'none',
          }}
          aria-label="Search for a location"
          aria-expanded={isOpen}
          aria-autocomplete="list"
        />

        {/* Search icon or loading spinner */}
        <span
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af',
            display: 'inline-flex',
            alignItems: 'center',
          }}
          aria-hidden="true"
        >
          {isLoading ? (
            // Lightweight CSS-only spinner — avoids shipping a 3rd-party
            // spinner. The animation is suppressed by the global
            // prefers-reduced-motion rule in App.css.
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                border: '2px solid #4b5563',
                borderTopColor: '#9ca3af',
                animation: 'location-search-spin 0.9s linear infinite',
              }}
            />
          ) : (
            <SearchIcon size={16} />
          )}
        </span>
      </div>

      {/* Error message */}
      {error && (
        <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
          {error}
        </div>
      )}

      {/* Results dropdown */}
      {isOpen && results.length > 0 && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto',
          }}
          role="listbox"
        >
          {results.map((result, index) => (
            <button
              key={result.place_id}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setSelectedIndex(index)}
              style={{
                width: '100%',
                padding: '10px 12px',
                textAlign: 'left',
                border: 'none',
                borderBottom: index < results.length - 1 ? '1px solid #f3f4f6' : 'none',
                backgroundColor: index === selectedIndex ? '#f3f4f6' : 'white',
                cursor: 'pointer',
                display: 'block',
              }}
              role="option"
              aria-selected={index === selectedIndex}
            >
              <div style={{ fontWeight: 500, fontSize: '14px', color: '#1f2937' }}>
                {getShortName(result.display_name)}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                {result.display_name}
              </div>
            </button>
          ))}

          {/* Attribution required by Nominatim */}
          <div
            style={{
              padding: '6px 12px',
              fontSize: '10px',
              color: '#9ca3af',
              backgroundColor: '#f9fafb',
              borderTop: '1px solid #e5e7eb',
            }}
          >
            Powered by OpenStreetMap Nominatim
          </div>
        </div>
      )}
    </div>
  );
}
